import { useEffect, useState } from 'react';

import { BuildingMinimap } from '@/components/BuildingMinimap';
import { PixiCanvas } from '@/components/PixiCanvas';
import { PixiTooltipLayer } from '@/components/PixiTooltipLayer';
import { useDashboardStore } from '@/state/store';
import type { PodPolicyAction, PodRuntimeState } from '@/state/store';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import type { PodSummary } from '@/api/rest';

/**
 * Pixi-backed pod room.
 *
 * Reuses the shared ``PixiCanvas`` (which switches between
 * ``BuildingExteriorScene`` and ``PodRoomScene`` based on
 * ``view.kind``).  Adds a thin DOM overlay on top of the canvas
 * carrying the back button, pod title and risk pill -- HTML wins
 * over Pixi for crisp button hit-areas, ARIA, and Esc bindings.
 */
const ROOM_LABEL: Record<string, string> = {
  canary_lab: 'Canary Lab',
  active_offices: 'Active Offices',
  reduced_floor: 'Reduced Floor',
  frozen_basement: 'Frozen Basement',
};

const RISK_COLOR: Record<string, string> = {
  active: '#2ea043',
  watch: '#d29922',
  reduced: '#db6d28',
  frozen: '#58a6ff',
};

export function PodRoomPixiView({ podId }: { podId: string }) {
  const goToBuilding = useDashboardStore((s) => s.goToBuilding);
  const pod = useDashboardStore((s) => s.pods[podId]);
  const summary = useDashboardStore((s) =>
    s.building?.floors.flatMap((f) => f.pods).find((p) => p.pod_id === podId),
  );
  const bp = useBreakpoint();
  const isMobile = bp === 'mobile';
  // Portfolio + Pod-stats panels overlap each other on any non-
  // desktop viewport (the canvas column between back-button and
  // event-feed is < 500 px wide on tablet too).  Collapse both by
  // default unless we have a roomy desktop.
  const isNarrow = bp !== 'desktop';

  // Esc -> back to building view.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') goToBuilding();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [goToBuilding]);

  const name = summary?.name ?? podId;
  const floor = pod?.floor ?? summary?.floor ?? 'active_offices';
  const riskState = pod?.riskState ?? summary?.risk_state ?? 'active';
  const riskAccent = RISK_COLOR[riskState] ?? '#8b949e';
  const haircut = pod?.haircutPct ?? summary?.haircut_pct ?? 0;

  return (
    <section
      style={{
        position: 'relative',
        minHeight: 0,
        minWidth: 0,
        display: 'grid',
        gridTemplateRows: 'auto 1fr',
        overflow: 'hidden',
      }}
    >
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: isMobile ? '8px 12px' : '10px 16px',
          background: '#161b22dd',
          borderBottom: '1px solid #21262d',
          backdropFilter: 'blur(8px)',
          zIndex: 2,
        }}
      >
        <button
          type="button"
          onClick={goToBuilding}
          aria-label="Back to building view (Esc)"
          title="Вернуться к виду всего здания (можно нажать Esc)"
          style={{
            background: '#21262d',
            color: '#e6edf3',
            border: '1px solid #30363d',
            borderRadius: 6,
            padding: isMobile ? '8px 12px' : '6px 10px',
            minHeight: isMobile ? 36 : 'auto',
            cursor: 'pointer',
            fontSize: 12,
            touchAction: 'manipulation',
            flexShrink: 0,
          }}
        >
          ← back
        </button>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              fontSize: 14,
              fontWeight: 600,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={`Имя пода (отображаемое). Под — это отдельный LLM-портфельный менеджер с собственными агентами (QA, SA, FA, Researcher, RM, EA) и капиталом.`}
          >
            {name}
          </div>
          <div
            style={{ fontSize: 11, color: '#8b949e' }}
            title={`pod_id: уникальный идентификатор. Этаж (floor): «комната», в которой живёт под — определяется его текущим risk_state.`}
          >
            {podId} · {ROOM_LABEL[floor] ?? floor}
          </div>
        </div>
        {pod?.alerts.errorCount && pod.alerts.errorCount > 0 ? (
          <AlertsBadge
            count={pod.alerts.errorCount}
            last={pod.alerts.last}
          />
        ) : null}
        <span
          title={
            riskState === 'frozen'
              ? 'FROZEN — risk officer заморозил под, новые сделки запрещены до разморозки. Обычно срабатывает по нескольким breach\'ам подряд (gross > limit, drawdown > limit и т.п.).'
              : riskState === 'reduced'
                ? 'REDUCED — рисковая «диета»: gross-лимит уменьшен. PM может торговать, но в более узких рамках. Haircut показывает размер сокращения.'
                : riskState === 'watch'
                  ? 'WATCH — под на наблюдении: один breach зафиксирован, при следующем уйдёт в reduced или frozen.'
                  : riskState === 'active'
                    ? 'ACTIVE — под в нормальном режиме, никаких ограничений сверх дефолтных лимитов.'
                    : 'Risk state ещё не получен из backend\'а.'
          }
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 11,
            padding: '4px 10px',
            background: '#0d1117',
            border: `1px solid ${riskAccent}`,
            borderRadius: 14,
            color: '#e6edf3',
            flexShrink: 0,
          }}
        >
          <span aria-hidden style={{ color: riskAccent, fontWeight: 700 }}>
            ●
          </span>
          {riskState}
          {haircut > 0 ? (
            <span
              style={{ color: '#8b949e' }}
              title={`Haircut: текущее снижение брутто-лимита, ${Math.round(haircut * 100)}%. Например 20% haircut на лимите 5.0 даёт эффективный лимит 4.0.`}
            >
              · haircut {Math.round(haircut * 100)}%
            </span>
          ) : null}
        </span>
      </header>
      <div style={{ position: 'relative', minHeight: 0 }}>
        <PixiCanvas>
          {/* Tooltip overlay -- mounted INSIDE PixiCanvas so it shares
              the host's coordinate space (it listens to pointermove on
              the parent div).  Pointer-events are explicitly disabled
              on the overlay so canvas interaction still flows through. */}
          <PixiTooltipLayer selectedPodId={podId} />
        </PixiCanvas>
        <BuildingMinimap selectedPodId={podId} />
        <PositionsPanel
          rows={selectPortfolioRows(pod).rows}
          live={selectPortfolioRows(pod).live}
          cashBalance={pod?.cashBalance ?? null}
          grossExposure={pod?.grossExposure ?? null}
          netExposure={pod?.netExposure ?? null}
          positionsAsOf={pod?.positionsAsOf ?? null}
          brokerSource={pod?.brokerSource ?? null}
          targetGross={pod?.pm.targetGross ?? null}
          decisionAt={pod?.pm.decisionAt ?? null}
          intentsCount={pod?.pm.intentsCount ?? null}
          isMobile={isMobile}
          isNarrow={isNarrow}
        />
        <StatsPanel
          summary={summary ?? null}
          utility={pod?.utility ?? null}
          riskState={riskState}
          riskSince={summary?.risk_since ?? null}
          riskReason={summary?.risk_reason ?? null}
          pmRationale={pod?.pm.lastRationale ?? null}
          pmConfidence={pod?.pm.confidence ?? null}
          navHistory={pod?.navHistory ?? []}
          disciplineFlag={pod?.disciplineFlag ?? null}
          centerbook={pod?.centerbook ?? null}
          centerbookAllocation={pod?.centerbookAllocation ?? null}
          policyAction={pod?.policyAction ?? null}
          lastActionFromBootstrap={summary?.last_action ?? null}
          isMobile={isMobile}
          isNarrow={isNarrow}
        />
      </div>
    </section>
  );
}

function AlertsBadge({
  count,
  last,
}: {
  count: number;
  last: NonNullable<ReturnType<typeof useDashboardStore.getState>['pods'][string]>['alerts']['last'];
}) {
  const tip = last
    ? `${last.kind.replace('_', ' ')}${last.ticker ? ` · ${last.ticker}` : ''} · ${last.reason}`
    : `${count} error${count === 1 ? '' : 's'}`;
  return (
    <span
      title={tip}
      aria-label={tip}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 11,
        padding: '4px 10px',
        background: '#3a1313',
        border: '1px solid #f85149',
        borderRadius: 14,
        color: '#ffd9d6',
        flexShrink: 0,
        cursor: 'help',
      }}
    >
      <span aria-hidden style={{ color: '#f85149', fontWeight: 800 }}>
        ⚠
      </span>
      {count} alert{count === 1 ? '' : 's'}
    </span>
  );
}

/**
 * Floating panel showing the PM's current portfolio composition.
 * Sources its data from ``pm.decision_made.data.top_weights`` --
 * the same wire field the backend already emits on every cycle.
 * Hidden when no decision has landed yet.
 */
/**
 * Pick the data source for the Portfolio panel.
 *
 * Prefers the live position book (maintained by ``position.opened``
 * / ``position.adjusted`` / ``position.closed`` events) over the
 * static ``pm.decision_made.top_weights`` snapshot.  The live book
 * stays current between PM decisions instead of freezing on the
 * last decision.  Falls back to the snapshot when no position
 * events have been observed yet.
 */
/** A normalised "row" the PositionsPanel can render uniformly,
 * whether it came from the live broker book (nav_tick.positions) or
 * the stale PM-decision target snapshot.
 *
 * ``qty`` / ``price`` / ``notional`` are only populated for live book
 * rows -- the PM target snapshot only knows weight.  Renderer must
 * cope with nulls gracefully (em-dash placeholders are fine here
 * because the *real* numbers will land on the next nav_tick). */
export interface PortfolioRow {
  ticker: string;
  weight: number;
  qty: number | null;
  price: number | null;
  notional: number | null;
  side: 'long' | 'short';
  /** FIGI / instrument-type only populated for the live broker book.
   * Surfaced as part of the ticker's hover tooltip when present. */
  figi: string | null;
  instrumentType: string | null;
}

function selectPortfolioRows(pod: PodRuntimeState | undefined): {
  rows: ReadonlyArray<PortfolioRow>;
  /** True when the data is from the live broker book (``nav_tick.positions``
   *  or streamed ``position.*`` events).  False when we're rendering the
   *  stale PM target snapshot as fallback. */
  live: boolean;
} {
  if (!pod) return { rows: [], live: false };
  const livePositions = Object.values(pod.positions);
  if (livePositions.length > 0) {
    // Live book has the richest data -- ship it through.  Sort by
    // |notional| first (broker-truth ordering) and fall back to
    // |weight| when notional isn't known yet (pure streaming pre-tick).
    const rows: PortfolioRow[] = livePositions.map((p) => ({
      ticker: p.ticker,
      weight: p.weight,
      qty: p.qty,
      price: p.price,
      notional: p.notional,
      side: p.side ?? (p.weight < 0 ? 'short' : 'long'),
      figi: p.figi,
      instrumentType: p.instrumentType,
    }));
    rows.sort((a, b) => {
      const an = a.notional != null ? Math.abs(a.notional) : 0;
      const bn = b.notional != null ? Math.abs(b.notional) : 0;
      if (an !== bn) return bn - an;
      return Math.abs(b.weight) - Math.abs(a.weight);
    });
    return { rows, live: true };
  }
  // Fallback: PM target snapshot (weights only, no broker truth).
  const rows: PortfolioRow[] = pod.pm.topWeights.map((row) => ({
    ticker: row.ticker,
    weight: row.weight,
    qty: null,
    price: null,
    notional: null,
    side: row.weight < 0 ? 'short' : 'long',
    figi: null,
    instrumentType: null,
  }));
  return { rows, live: false };
}

/** Compact-currency helper -- "1.6M", "452k", "−157k" for chips. */
function fmtRub(value: number | null): string {
  if (value == null || !Number.isFinite(value)) return '—';
  const sign = value < 0 ? '−' : '';
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `${sign}${(abs / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `${sign}${(abs / 1_000).toFixed(0)}k`;
  return `${sign}${abs.toFixed(0)}`;
}

function fmtQty(value: number | null): string {
  if (value == null || !Number.isFinite(value)) return '—';
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `${value < 0 ? '−' : ''}${(abs / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `${value < 0 ? '−' : ''}${(abs / 1_000).toFixed(1)}k`;
  // Sub-thousand qty -- show whole units, signed.
  return value.toFixed(0);
}

function fmtPrice(value: number | null): string {
  if (value == null || !Number.isFinite(value)) return '—';
  // Roubles per share -- 2 decimals for most equities, more for cheap
  // names whose price is well below 10.
  return value < 10 ? value.toFixed(3) : value.toFixed(2);
}

function PositionsPanel({
  rows,
  live,
  cashBalance,
  grossExposure,
  netExposure,
  positionsAsOf,
  brokerSource,
  targetGross,
  decisionAt,
  intentsCount,
  isMobile,
  isNarrow,
}: {
  rows: ReadonlyArray<PortfolioRow>;
  /** True when the data is from the live broker book (nav_tick.positions
   *  or per-trade ``position.*`` events).  Drives the "LIVE" header tag. */
  live: boolean;
  /** Latest cash balance from nav_tick.cash_balance, in account currency. */
  cashBalance: number | null;
  /** Latest gross_exposure / net_exposure from nav_tick. */
  grossExposure: number | null;
  netExposure: number | null;
  /** ``positionsAsOf`` (when the broker snapshot was taken) -- used to
   *  caption the panel with a "snapshot at HH:MM:SS" line so operators
   *  know whether they're looking at a fresh or stale view. */
  positionsAsOf: string | null;
  /** ``tinkoff_get_portfolio`` / ``market_snapshot_fallback`` / ``null``.
   *  Fallback source surfaces a warning chip so users don't trust
   *  numbers that were synthesised from market quotes. */
  brokerSource: string | null;
  targetGross: number | null;
  decisionAt: string | null;
  intentsCount: number | null;
  isMobile: boolean;
  isNarrow: boolean;
}) {
  // On mobile and narrow tablet the canvas is small -- start
  // collapsed so the panel only takes its header row and lets the
  // PM / agents breathe.  Tap the header to reveal the full list.
  const collapsibleHere = isNarrow;
  const collapsedCount = isMobile ? 5 : 8;
  const [collapsedMobile, setCollapsedMobile] = useState(collapsibleHere);
  const [expanded, setExpanded] = useState(false);
  if (rows.length === 0) return null;

  // Bar scale: use the broker-truth |notional| when we have it,
  // otherwise fall back to |weight|.  Mixing the two would make the
  // bars visually meaningless.
  const haveNotional = rows.some((r) => r.notional != null);
  const maxAbsBar = rows.reduce((acc, row) => {
    const value = haveNotional
      ? Math.abs(row.notional ?? 0)
      : Math.abs(row.weight);
    return Math.max(acc, value);
  }, 1e-6);

  const decisionAge = decisionAt
    ? relAge(Date.now() - new Date(decisionAt).getTime())
    : null;
  const snapshotAge = positionsAsOf
    ? relAge(Date.now() - new Date(positionsAsOf).getTime())
    : null;

  const visible = expanded ? rows : rows.slice(0, collapsedCount);
  const hiddenCount = rows.length - visible.length;

  // Aggregates for the long/short composition bar + totals line.
  const longTotalWeight = rows.reduce(
    (acc, row) => (row.weight > 0 ? acc + row.weight : acc),
    0,
  );
  const shortTotalWeight = rows.reduce(
    (acc, row) => (row.weight < 0 ? acc + Math.abs(row.weight) : acc),
    0,
  );
  const longTotalNotional = rows.reduce(
    (acc, row) =>
      row.notional != null && row.notional > 0 ? acc + row.notional : acc,
    0,
  );
  const shortTotalNotional = rows.reduce(
    (acc, row) =>
      row.notional != null && row.notional < 0
        ? acc + Math.abs(row.notional)
        : acc,
    0,
  );
  const totalGrossWeight = longTotalWeight + shortTotalWeight;

  const isFallback = brokerSource === 'market_snapshot_fallback';

  return (
    <aside
      aria-label="Current portfolio composition"
      style={{
        position: 'absolute',
        left: isMobile ? 8 : 12,
        top: isMobile ? 8 : 12,
        width: isMobile ? 'calc(100% - 16px)' : 280,
        maxWidth: 320,
        background: '#0d1117ee',
        border: '1px solid #30363d',
        borderRadius: 8,
        padding: isMobile ? 8 : 10,
        backdropFilter: 'blur(4px)',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.45)',
        zIndex: 3,
        fontSize: 11,
        maxHeight: expanded ? '78%' : 'auto',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <header
        onClick={collapsibleHere ? () => setCollapsedMobile((v) => !v) : undefined}
        role={collapsibleHere ? 'button' : undefined}
        aria-expanded={collapsibleHere ? !collapsedMobile : undefined}
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 8,
          marginBottom: 4,
          cursor: collapsibleHere ? 'pointer' : 'default',
          userSelect: 'none',
        }}
      >
        <strong
          style={{
            color: '#8b949e',
            fontSize: 9,
            textTransform: 'uppercase',
            letterSpacing: 0.8,
          }}
          title={`${rows.length} реальных позиций в брокере (включая «хвост» от прошлых циклов). Сортировка — по абсолютному notional.`}
        >
          {collapsibleHere ? (collapsedMobile ? '▸ ' : '▾ ') : null}
          Portfolio · {rows.length}
          {live ? (
            <span
              title="LIVE — данные с брокера (Tinkoff get_portfolio). Обновляются на каждом pod.nav_tick после run_cycle."
              style={{
                marginLeft: 6,
                padding: '0 4px',
                fontSize: 8,
                fontWeight: 700,
                color: '#3fb950',
                background: '#3fb95022',
                border: '1px solid #3fb95066',
                borderRadius: 3,
                letterSpacing: 0.6,
              }}
            >
              LIVE
            </span>
          ) : (
            <span
              title="TARGET — показан план PM из последнего pm.decision_made (broker snapshot ещё не пришёл). Это намерения, не фактические позиции."
              style={{
                marginLeft: 6,
                padding: '0 4px',
                fontSize: 8,
                fontWeight: 700,
                color: '#d29922',
                background: '#d2992222',
                border: '1px solid #d2992266',
                borderRadius: 3,
                letterSpacing: 0.6,
              }}
            >
              TARGET
            </span>
          )}
          {isFallback ? (
            <span
              title="FALLBACK — broker недоступен, цифры реконструированы из рыночных котировок + последних известных весов. Не доверяй для риск-оценки."
              style={{
                marginLeft: 4,
                padding: '0 4px',
                fontSize: 8,
                fontWeight: 700,
                color: '#f85149',
                background: '#f8514922',
                border: '1px solid #f8514966',
                borderRadius: 3,
                letterSpacing: 0.6,
              }}
            >
              FALLBACK
            </span>
          ) : null}
        </strong>
        <span style={{ color: '#6e7681', fontSize: 9, textAlign: 'right' }}>
          {grossExposure != null ? (
            <span title="Брутто-экспозиция = Σ|notional|/capital. Это плечо: gross 5.21 = под держит позиций на 5.2× капитала. Считает margin engine брокера.">
              gross {grossExposure.toFixed(2)}
            </span>
          ) : targetGross != null ? (
            <span title="Целевая брутто-экспозиция из текущего PM-плана (gross broker-truth не получен).">
              gross {targetGross.toFixed(2)} (tgt)
            </span>
          ) : null}
          {netExposure != null ? (
            <>
              {' · '}
              <span title="Нетто-экспозиция = Σ(notional)/capital, знаковая. +0.88 = под суммарно длинный на 88% капитала; -0.20 = чистый шорт; 0.0 = market-neutral.">
                net {netExposure >= 0 ? '+' : ''}
                {netExposure.toFixed(2)}
              </span>
            </>
          ) : null}
          {intentsCount != null ? (
            <>
              {' · '}
              <span title={`PM в этом цикле хочет ${intentsCount} тикеров (target portfolio). Если intents < позиций — у пода накопился хвост позиций, которые PM не закладывает в новый план, но пока и не закрывает.`}>
                {intentsCount} intents
              </span>
            </>
          ) : null}
        </span>
      </header>

      {/* Secondary caption: snapshot age + cash balance.  Two
          independent facts, joined with " · " so they wrap together
          when the panel gets narrow. */}
      {(snapshotAge || cashBalance != null || (!live && decisionAge)) && (
        <div
          style={{
            color: '#6e7681',
            fontSize: 9,
            marginBottom: 6,
            display: 'flex',
            gap: 6,
            flexWrap: 'wrap',
          }}
        >
          {live && snapshotAge ? (
            <span title="Возраст broker-snapshot'а. Меньше 15 минут — доверяй полностью. 30+ минут — брокер запаздывал, рынок мог уйти.">
              book {snapshotAge}
            </span>
          ) : null}
          {!live && decisionAge ? (
            <span title="Возраст последнего PM-решения. Это намерения PM, а не фактические позиции.">
              target {decisionAge}
            </span>
          ) : null}
          {cashBalance != null ? (
            <>
              <span style={{ color: '#30363d' }}>·</span>
              <span title="Кэш на брокерском счёте. Может быть больше NAV: при шортах брокер начисляет cash от продажи, а ты остаёшься должен акции (отрицательный notional).">
                cash {fmtRub(cashBalance)} ₽
              </span>
            </>
          ) : null}
        </div>
      )}

      {/* Long / short composition bar so dozens of positions still
          telegraph "this pod is 70/30 long-short" at a glance. */}
      <div
        title={
          haveNotional
            ? `long ${fmtRub(longTotalNotional)} ₽ · short ${fmtRub(shortTotalNotional)} ₽`
            : `long ${(longTotalWeight * 100).toFixed(1)}% · short ${(shortTotalWeight * 100).toFixed(1)}%`
        }
        style={{
          display: 'flex',
          height: 5,
          borderRadius: 3,
          overflow: 'hidden',
          marginBottom: collapsibleHere && collapsedMobile ? 0 : 6,
          background: '#161b22',
        }}
      >
        <span
          style={{
            width:
              totalGrossWeight > 0
                ? `${(longTotalWeight / totalGrossWeight) * 100}%`
                : '0%',
            background: '#2ea043',
          }}
        />
        <span
          style={{
            width:
              totalGrossWeight > 0
                ? `${(shortTotalWeight / totalGrossWeight) * 100}%`
                : '0%',
            background: '#f85149',
          }}
        />
      </div>

      {collapsibleHere && collapsedMobile ? null : (
        <ol
          style={{
            margin: 0,
            padding: 0,
            listStyle: 'none',
            overflowY: expanded ? 'auto' : 'visible',
            flex: 1,
            minHeight: 0,
          }}
        >
          {visible.map((row) => {
            const isShort = row.side === 'short';
            const barValue = haveNotional
              ? Math.abs(row.notional ?? 0)
              : Math.abs(row.weight);
            const pct = (barValue / maxAbsBar) * 100;
            const color = isShort ? '#f85149' : '#2ea043';
            return (
              <li
                key={row.ticker}
                style={{
                  display: 'grid',
                  // ticker | bar | weight%   (line 1)
                  // (empty)| qty @ price · notional   (line 2, small)
                  gridTemplateColumns: '52px 1fr auto',
                  alignItems: 'center',
                  gap: 6,
                  rowGap: 1,
                  padding: '3px 0',
                  fontSize: 11,
                  borderTop: '1px solid #161b22',
                }}
              >
                <span
                  style={{ color: '#e6edf3', fontWeight: 600, gridRow: '1' }}
                  title={
                    row.figi
                      ? `${row.ticker} (FIGI ${row.figi}) — ${row.side === 'short' ? 'short' : 'long'}-позиция${row.instrumentType ? `, тип: ${row.instrumentType}` : ''}.`
                      : `${row.ticker} — ${row.side === 'short' ? 'short' : 'long'}-позиция.`
                  }
                >
                  {row.ticker}
                </span>
                <span
                  style={{
                    height: 6,
                    background: '#161b22',
                    borderRadius: 3,
                    position: 'relative',
                    overflow: 'hidden',
                    gridRow: '1',
                  }}
                  title={`Длина бара — доля в брутто-экспозиции (${haveNotional ? 'по |notional|' : 'по |weight|'}). Самый широкий бар = крупнейшая позиция пода.`}
                >
                  <span
                    style={{
                      position: 'absolute',
                      inset: 0,
                      width: `${pct}%`,
                      background: color,
                      borderRadius: 3,
                    }}
                  />
                </span>
                <span
                  style={{
                    color,
                    fontVariantNumeric: 'tabular-nums',
                    minWidth: 44,
                    textAlign: 'right',
                    gridRow: '1',
                  }}
                  title={`weight = notional / capital. Знаковое: ${row.weight >= 0 ? '+' : ''}${(row.weight * 100).toFixed(2)}% означает что под держит позицию размером ${Math.abs(row.weight * 100).toFixed(1)}% капитала ${row.weight < 0 ? 'в шорт' : 'в лонг'}. Сравнимо с pm.decision_made.top_weights.`}
                >
                  {(row.weight * 100).toFixed(1)}%
                </span>
                {/* Second row: broker-truth detail.  Only shown when we
                    have at least one of qty / price / notional, so the
                    fallback PM-target view stays a tidy single line per
                    ticker. */}
                {row.qty != null || row.price != null || row.notional != null ? (
                  <span
                    style={{
                      gridColumn: '2 / span 2',
                      gridRow: '2',
                      color: '#8b949e',
                      fontSize: 9.5,
                      fontVariantNumeric: 'tabular-nums',
                      display: 'flex',
                      gap: 6,
                      flexWrap: 'wrap',
                    }}
                  >
                    <span
                      title={`${row.qty != null ? `qty: ${row.qty < 0 ? 'отрицательное (шорт)' : 'положительное (лонг)'} — количество акций. ` : ''}${row.price != null ? `price: текущая mark-to-market цена за акцию из брокера, в ₽.` : 'price: not available.'}`}
                    >
                      {fmtQty(row.qty)} @ {fmtPrice(row.price)} ₽
                    </span>
                    {row.notional != null ? (
                      <>
                        <span style={{ color: '#30363d' }}>·</span>
                        <span
                          style={{ color: isShort ? '#f85149aa' : '#2ea043aa' }}
                          title={`notional = qty × price = ${row.notional.toLocaleString('ru-RU', { maximumFractionDigits: 2 })} ₽. Знаковое (шорт → отрицательное). Используй как «сколько денег в этой позиции» — самая интуитивная метрика размера.`}
                        >
                          {fmtRub(row.notional)} ₽
                        </span>
                      </>
                    ) : null}
                  </span>
                ) : null}
              </li>
            );
          })}
        </ol>
      )}

      {(!collapsibleHere || !collapsedMobile) && hiddenCount > 0 ? (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          style={{
            marginTop: 6,
            background: 'transparent',
            border: '1px dashed #30363d',
            color: '#8b949e',
            borderRadius: 4,
            padding: '3px 8px',
            fontSize: 10,
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          + {hiddenCount} more position{hiddenCount === 1 ? '' : 's'}
        </button>
      ) : null}
      {expanded && rows.length > collapsedCount ? (
        <button
          type="button"
          onClick={() => setExpanded(false)}
          style={{
            marginTop: 6,
            background: 'transparent',
            border: '1px solid #30363d',
            color: '#8b949e',
            borderRadius: 4,
            padding: '3px 8px',
            fontSize: 10,
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          collapse
        </button>
      ) : null}
    </aside>
  );
}

function relAge(ms: number): string {
  if (Number.isNaN(ms) || ms < 0) return 'now';
  if (ms < 60_000) return `${Math.floor(ms / 1_000)}s ago`;
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
  return `${Math.floor(ms / 3_600_000)}h ago`;
}

/**
 * Compact stats panel pinned to the top-right of the pod-room
 * canvas (below the header strip).  Surfaces backend data the
 * dashboard otherwise wouldn't display:
 *
 *   * ``daily_return`` / ``daily_pnl`` -- today's performance.
 *   * ``nav`` -- absolute portfolio value.
 *   * ``utility.recorded.total`` -- the cumulative utility score
 *     after the last cycle.  Bars below break down the major
 *     components (alpha / downside / turnover).
 *   * ``risk_state + risk_reason + risk_since`` -- when the pod is
 *     not ``active``, explain why and for how long.  This is
 *     critical context the existing risk pill alone doesn't carry.
 */
function StatsPanel({
  summary,
  utility,
  riskState,
  riskSince,
  riskReason,
  pmRationale,
  pmConfidence,
  navHistory,
  disciplineFlag,
  centerbook,
  centerbookAllocation,
  policyAction,
  lastActionFromBootstrap,
  isMobile,
  isNarrow,
}: {
  summary: PodSummary | null;
  utility: PodRuntimeState['utility'];
  riskState: string;
  riskSince: string | null;
  riskReason: string | null;
  pmRationale: string | null;
  pmConfidence: number | null;
  navHistory: readonly {
    ts: string;
    nav: number;
    intradayReturn: number | null;
    brokerScaleIntradayReturn?: number | null;
  }[];
  disciplineFlag: PodRuntimeState['disciplineFlag'];
  centerbook: PodRuntimeState['centerbook'];
  centerbookAllocation: PodRuntimeState['centerbookAllocation'];
  policyAction: PodRuntimeState['policyAction'];
  lastActionFromBootstrap: Record<string, unknown> | null;
  /** True on mobile breakpoint -- still drives padding / collapsed-count
   *  heuristics.  ``isNarrow`` (mobile + tablet) drives the click-to-
   *  expand panel collapse instead, since the panels overlap on tablet
   *  too. */
  isMobile: boolean;
  isNarrow: boolean;
}) {
  const [rationaleOpen, setRationaleOpen] = useState(false);
  // Mirror the Portfolio panel's collapse pattern.  Default-collapse on
  // any non-desktop viewport (mobile + tablet) because the two stacked
  // panels otherwise overlap each other on narrow canvases.
  const collapsibleHere = isNarrow;
  const [collapsedMobile, setCollapsedMobile] = useState(collapsibleHere);
  const dailyReturn = summary?.daily_return ?? null;
  const dailyPnl = summary?.daily_pnl ?? null;
  // Prefer the live nav tick over the REST snapshot for the headline
  // number; falls back to the bootstrap snapshot when the WS hasn't
  // delivered a ``pod.nav_tick`` yet.
  const lastTick = navHistory.length > 0 ? navHistory[navHistory.length - 1] : null;
  const liveNav = lastTick?.nav ?? null;
  // Headline %: prefer the broker-scale return when the backend
  // computed it (only available when broker reports a sandbox-topup
  // adjusted snapshot).  Economic-scale return remains the fallback
  // for compatibility and pods without topups (where the two scales
  // coincide anyway).  See ``pod.nav_tick`` schema for the math:
  // a +2% broker day reads as +30%+ on economic scale for a 15x-
  // topped-up pod -- broker scale gives operators the realistic
  // single-digit number a real trader would see.
  const liveBrokerScaleReturn = lastTick?.brokerScaleIntradayReturn ?? null;
  const liveEconomicReturn = lastTick?.intradayReturn ?? null;
  const liveReturn = liveBrokerScaleReturn ?? liveEconomicReturn;
  const nav = liveNav ?? summary?.nav ?? null;
  const displayReturn = liveReturn ?? dailyReturn ?? null;
  // Keep the economic value around for tooltip context when both
  // scales are known and meaningfully differ (>1.5x).  Surfacing the
  // raw artifact gives operators the full picture.
  const showEconomicCompanion =
    liveBrokerScaleReturn != null &&
    liveEconomicReturn != null &&
    Math.abs(liveEconomicReturn) > 1.5 * Math.abs(liveBrokerScaleReturn) + 0.01;
  const utilityTotal = utility?.total ?? summary?.utility_today ?? null;
  const utilityComponents = utility?.components ?? null;
  const showRiskReason = riskState !== 'active' && (riskReason || riskSince);
  const modifiers = collectModifiers(summary?.current_modifiers ?? null);
  // Prefer the live event-stream policy action; fall back to the
  // REST bootstrap's ``last_action`` when no event has fired yet
  // (the snapshot keeps the most recent applied/rolled_back across
  // a page reload).
  const effectivePolicy =
    policyAction ?? parseLastActionFromBootstrap(lastActionFromBootstrap);
  // Don't render the panel at all when there's literally nothing to show.
  if (
    displayReturn == null &&
    dailyPnl == null &&
    nav == null &&
    utilityTotal == null &&
    !showRiskReason &&
    !pmRationale &&
    navHistory.length === 0 &&
    !disciplineFlag &&
    !centerbook &&
    !centerbookAllocation &&
    !effectivePolicy &&
    modifiers.length === 0
  ) {
    return null;
  }
  // On mobile, dock to the bottom-right instead of top-right so the
  // panel doesn't fight the Portfolio panel that already lives top.
  return (
    <aside
      aria-label="Pod stats"
      style={{
        position: 'absolute',
        right: isMobile ? 8 : 12,
        top: isMobile ? 'auto' : 12,
        bottom: isMobile ? 60 : 'auto',
        width: isMobile ? 'auto' : 200,
        maxWidth: 220,
        background: '#0d1117ee',
        border: '1px solid #30363d',
        borderRadius: 8,
        padding: isMobile ? 8 : 10,
        backdropFilter: 'blur(4px)',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.45)',
        zIndex: 3,
        fontSize: 11,
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}
    >
      <header
        onClick={collapsibleHere ? () => setCollapsedMobile((v) => !v) : undefined}
        role={collapsibleHere ? 'button' : undefined}
        aria-expanded={collapsibleHere ? !collapsedMobile : undefined}
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 8,
          color: '#8b949e',
          fontSize: 9,
          textTransform: 'uppercase',
          letterSpacing: 0.8,
          cursor: collapsibleHere ? 'pointer' : 'default',
          userSelect: 'none',
        }}
      >
        <span
          title="Сводка по производительности и риск-состоянию пода: Today % (intraday return), NAV, Utility-скор, состояние policy/discipline/centerbook overlay. Кликай на блоки для деталей."
        >
          {collapsibleHere ? (collapsedMobile ? '▸ ' : '▾ ') : null}
          Pod stats
        </span>
        {/* Compact collapsed summary -- one inline glance of today's
            return so users don't have to expand to see it. */}
        {collapsibleHere && collapsedMobile && displayReturn != null ? (
          <span
            style={{
              color: displayReturn >= 0 ? '#3fb950' : '#f85149',
              fontWeight: 700,
              fontSize: 10,
              textTransform: 'none',
              letterSpacing: 0,
            }}
          >
            {displayReturn >= 0 ? '+' : ''}
            {(displayReturn * 100).toFixed(2)}%
          </span>
        ) : null}
      </header>
      {collapsibleHere && collapsedMobile ? null : (
      <>
      {displayReturn != null ? (
        <StatRow
          label={liveBrokerScaleReturn != null ? 'Today · broker' : 'Today'}
          value={`${displayReturn >= 0 ? '+' : ''}${(displayReturn * 100).toFixed(2)}%`}
          color={displayReturn >= 0 ? '#3fb950' : '#f85149'}
          sub={
            // Composite sub-line: rouble P&L (always) + economic % in
            // parentheses when it diverges from the headline broker %.
            // The economic value is mathematically right but visually
            // misleading on heavily-topped-up sandbox pods -- showing
            // both side-by-side lets operators see the gap without
            // hiding the underlying calculation.
            (() => {
              const pnlPart =
                dailyPnl != null
                  ? `${dailyPnl >= 0 ? '+' : ''}₽${Math.round(dailyPnl).toLocaleString('en-US')}`
                  : null;
              const econPart = showEconomicCompanion && liveEconomicReturn != null
                ? `econ ${liveEconomicReturn >= 0 ? '+' : ''}${(liveEconomicReturn * 100).toFixed(1)}%`
                : null;
              if (pnlPart && econPart) return `${pnlPart} · ${econPart}`;
              return pnlPart ?? econPart;
            })()
          }
          tooltip={
            liveBrokerScaleReturn != null
              ? 'Intraday return на брокерской NAV-шкале: intraday_pnl ÷ broker_open_nav. Это «что увидел бы реальный трейдер»: типичные дневные движения single-digit. Sub-строка показывает абсолютный P&L в рублях и (если сильно отличается) economic-шкалу в скобках — тот же P&L, поделённый на экономический NAV (broker NAV минус SandboxPayIn-топапы), который для leveraged подов сильно занижен и амплифицирует %.'
              : 'Intraday return = (текущий NAV − NAV на открытии сессии) / NAV на открытии. На брокерской шкале (broker-scale) пока недоступен — broker не отдал свежий snapshot. Backend клампит экономический return до ±20% для PolicyUpdater/CycleUtilityScorer; для дисплея клампа нет.'
          }
          subTooltip="Абсолютный intraday-P&L в рублях — это РЕАЛЬНАЯ цифра broker-движения NAV, идентичная на обеих шкалах (топап-дельта стабильна за сессию)."
        />
      ) : null}
      {navHistory.length >= 2 ? <NavSparkline ticks={navHistory} /> : null}
      {nav != null ? (
        <StatRow
          label="NAV"
          value={`₽${Math.round(nav).toLocaleString('en-US')}`}
          color="#e6edf3"
          sub={
            summary?.capital
              ? `cap ₽${Math.round(summary.capital).toLocaleString('en-US')}`
              : null
          }
          tooltip="Net Asset Value — стоимость пода. В sandbox-режиме это «экономический NAV» (broker NAV минус сумма SandboxPayIn-топапов), на реальном счёте — просто broker NAV."
          subTooltip="cap — стартовый капитал пода (как назначен в pod_config). NAV / cap = накопленный return пода с момента создания."
        />
      ) : null}
      {utilityTotal != null ? (
        <>
          <StatRow
            label="Utility"
            value={`${utilityTotal >= 0 ? '+' : ''}${utilityTotal.toFixed(3)}`}
            color={utilityTotal >= 0 ? '#3fb950' : '#f85149'}
            sub={utility?.recordedAt ? relAge(Date.now() - new Date(utility.recordedAt).getTime()) : null}
            tooltip="Utility score — оценка PM-цикла от CycleUtilityScorer. Складывается из alpha + carry − downside − turnover. Используется PolicyUpdater'ом для решений R1-R5 (увеличить/уменьшить лимиты, заморозить и т.п.)."
            subTooltip="Когда последний раз считался utility-score (обычно после run_cycle)."
          />
          {utilityComponents ? (
            <UtilityComponents components={utilityComponents} />
          ) : null}
        </>
      ) : null}
      {showRiskReason ? (
        <div
          style={{
            marginTop: 4,
            padding: '6px 8px',
            background: '#21262d',
            borderRadius: 4,
            fontSize: 10,
            color: '#c9d1d9',
            lineHeight: 1.35,
          }}
        >
          <div style={{ color: '#8b949e', marginBottom: 2 }}>
            Why {riskState}
          </div>
          {riskReason ? (
            <div style={{ fontWeight: 600 }}>{prettyReason(riskReason)}</div>
          ) : null}
          {riskSince ? (
            <div style={{ color: '#6e7681', fontSize: 9 }}>
              {relAge(Date.now() - new Date(riskSince).getTime())}
            </div>
          ) : null}
        </div>
      ) : null}
      {disciplineFlag ? <DisciplineRow flag={disciplineFlag} /> : null}
      {centerbook ? <CenterbookRow ovr={centerbook} /> : null}
      {centerbookAllocation ? (
        <AllocationRow alloc={centerbookAllocation} />
      ) : null}
      {effectivePolicy ? <PolicyRow action={effectivePolicy} /> : null}
      {modifiers.length > 0 ? <ModifiersChips modifiers={modifiers} /> : null}
      {pmRationale ? (
        <button
          type="button"
          onClick={() => setRationaleOpen((v) => !v)}
          aria-expanded={rationaleOpen}
          style={{
            marginTop: 4,
            padding: '6px 8px',
            background: '#161b22',
            border: '1px solid #21262d',
            borderRadius: 4,
            fontSize: 10,
            color: '#c9d1d9',
            cursor: 'pointer',
            textAlign: 'left',
            fontFamily: 'inherit',
            lineHeight: 1.4,
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              gap: 6,
            }}
          >
            <span
              style={{ color: '#8b949e' }}
              title="Объяснение PM (Portfolio Manager) почему он принял текущее решение: какие сигналы агентов учёл, как балансировал риск/доходность. Берётся из pm.decision_made.rationale."
            >
              PM rationale
            </span>
            {pmConfidence != null ? (
              <span
                style={{ color: '#6e7681', fontSize: 9 }}
                title="Уверенность PM в текущем решении (0-100%). Низкая (<50%) — PM сомневается, рынок неясный. Высокая (>80%) — сильный сигнал, скорее всего сходящиеся outputs агентов."
              >
                conf {(pmConfidence * 100).toFixed(0)}%
              </span>
            ) : null}
            <span style={{ color: '#6e7681' }}>{rationaleOpen ? '▾' : '▸'}</span>
          </div>
          {rationaleOpen ? (
            <div style={{ marginTop: 4, color: '#e6edf3' }}>{pmRationale}</div>
          ) : (
            <div
              style={{
                marginTop: 2,
                color: '#8b949e',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {pmRationale.slice(0, 60)}
              {pmRationale.length > 60 ? '…' : ''}
            </div>
          )}
        </button>
      ) : null}
      </>
      )}
    </aside>
  );
}

/**
 * Compact intraday NAV sparkline.  Draws an SVG polyline + an area
 * fill underneath using the last N ``pod.nav_tick`` envelopes the
 * store has accumulated.  Color tracks the sign of the latest tick's
 * intraday return.
 */
function NavSparkline({
  ticks,
}: {
  ticks: readonly { ts: string; nav: number; intradayReturn: number | null }[];
}) {
  const w = 200;
  const h = 32;
  const pad = 2;
  if (ticks.length < 2) return null;
  const navs = ticks.map((t) => t.nav);
  const minNav = Math.min(...navs);
  const maxNav = Math.max(...navs);
  const range = Math.max(1e-9, maxNav - minNav);
  const lastTick = ticks[ticks.length - 1];
  const isUp = (lastTick?.intradayReturn ?? 0) >= 0;
  const color = isUp ? '#3fb950' : '#f85149';
  const pts = ticks.map((t, i) => {
    const x = pad + (i / (ticks.length - 1)) * (w - pad * 2);
    const y = pad + (1 - (t.nav - minNav) / range) * (h - pad * 2);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  const polyline = pts.join(' ');
  const lastPt = pts[pts.length - 1]?.split(',');
  return (
    <svg
      width="100%"
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      aria-label={`NAV history, ${ticks.length} ticks`}
      style={{ marginTop: 2 }}
    >
      <polyline
        points={`${pts[0]},${pad + h - pad * 2} ${polyline} ${pts[pts.length - 1]?.split(',')[0]},${pad + h - pad * 2}`}
        fill={`${color}22`}
        stroke="none"
      />
      <polyline points={polyline} fill="none" stroke={color} strokeWidth={1.6} />
      {lastPt ? (
        <circle cx={Number(lastPt[0])} cy={Number(lastPt[1])} r={2.2} fill={color} />
      ) : null}
    </svg>
  );
}

function StatRow({
  label,
  value,
  color,
  sub,
  /** Optional native ``title=`` tooltip explaining what this metric
   * means.  Long-form Russian by convention -- we want operators to
   * be able to learn the dashboard from tooltips alone. */
  tooltip,
  subTooltip,
}: {
  label: string;
  value: string;
  color: string;
  sub: string | null;
  tooltip?: string;
  subTooltip?: string;
}) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        alignItems: 'baseline',
        gap: 6,
      }}
    >
      <span
        style={{ color: '#8b949e', fontSize: 10 }}
        title={tooltip}
      >
        {label}
      </span>
      <div style={{ textAlign: 'right' }}>
        <div
          style={{
            color,
            fontWeight: 700,
            fontVariantNumeric: 'tabular-nums',
            fontSize: 12,
          }}
          title={tooltip}
        >
          {value}
        </div>
        {sub ? (
          <div
            style={{ color: '#6e7681', fontSize: 9 }}
            title={subTooltip}
          >
            {sub}
          </div>
        ) : null}
      </div>
    </div>
  );
}

// Distinct hue per component so individual segments in the stacked
// bar stay visually separate.  Positive components lean green/teal,
// negative components lean red/orange.
const UTILITY_COLOR: Record<string, string> = {
  alpha: '#3fb950',
  carry: '#2ea043',
  downside: '#f85149',
  turnover: '#db6d28',
  inactivity: '#a371f7',
  concentration: '#bf8700',
  execution_rejection: '#d2691e',
  risk_state_penalty: '#e84855',
};

function utilityColor(name: string, isNegative: boolean): string {
  const explicit = UTILITY_COLOR[name];
  if (explicit) return explicit;
  return isNegative ? '#f85149' : '#3fb950';
}

/**
 * Mini divergent stacked-bar chart for utility components.  Backend
 * payload is ``{alpha, downside, turnover, inactivity, ...}`` -- a mix
 * of positive contributions (alpha) and penalties (downside, turnover).
 *
 * - Bar split at center: left = negative drags, right = positive gains
 * - Each segment is a distinct colour so the largest drag/gain is
 *   readable at a glance
 * - Click the bar to expand a numbers table with every component
 */
function UtilityComponents({
  components,
}: {
  components: Record<string, number>;
}) {
  const [expanded, setExpanded] = useState(false);
  const entries = Object.entries(components).filter(([, v]) =>
    Number.isFinite(v) && Math.abs(v) > 1e-9,
  );
  if (entries.length === 0) return null;
  const positives = entries
    .filter(([, v]) => v > 0)
    .sort(([, a], [, b]) => b - a);
  const negatives = entries
    .filter(([, v]) => v < 0)
    .sort(([, a], [, b]) => a - b); // most-negative first
  const posSum = positives.reduce((acc, [, v]) => acc + v, 0);
  const negSum = Math.abs(negatives.reduce((acc, [, v]) => acc + v, 0));
  const half = Math.max(posSum, negSum, 1e-6);
  // The bar is 100% wide.  Center is at 50%.  Each segment occupies
  // (|v| / half) * 50% on its side, leaving the unused space empty.
  const tip = entries
    .map(([n, v]) => `${n.replace(/_/g, ' ')}: ${v >= 0 ? '+' : ''}${v.toFixed(4)}`)
    .join('\n');
  return (
    <div style={{ marginTop: 4 }}>
      <button
        type="button"
        title={tip}
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        aria-label="Utility components breakdown"
        style={{
          display: 'block',
          width: '100%',
          padding: 0,
          margin: 0,
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
          fontFamily: 'inherit',
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 0,
            height: 10,
            background: '#161b22',
            borderRadius: 3,
            overflow: 'hidden',
            border: '1px solid #21262d',
          }}
        >
          {/* Negative side -- segments grow right-to-left from the centre */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'row-reverse',
              alignItems: 'stretch',
              justifyContent: 'flex-start',
              minWidth: 0,
            }}
          >
            {negatives.map(([name, value]) => {
              const pct = (Math.abs(value) / half) * 100;
              return (
                <span
                  key={`neg-${name}`}
                  title={`${name.replace(/_/g, ' ')}: ${value.toFixed(4)}`}
                  style={{
                    width: `${pct}%`,
                    background: utilityColor(name, true),
                    borderRight: '1px solid #0d1117',
                  }}
                />
              );
            })}
          </div>
          {/* Positive side -- segments grow left-to-right from the centre */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'stretch',
              justifyContent: 'flex-start',
              minWidth: 0,
              borderLeft: '1px solid #30363d',
            }}
          >
            {positives.map(([name, value]) => {
              const pct = (value / half) * 100;
              return (
                <span
                  key={`pos-${name}`}
                  title={`${name.replace(/_/g, ' ')}: +${value.toFixed(4)}`}
                  style={{
                    width: `${pct}%`,
                    background: utilityColor(name, false),
                    borderRight: '1px solid #0d1117',
                  }}
                />
              );
            })}
          </div>
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            color: '#6e7681',
            fontSize: 9,
            marginTop: 2,
          }}
        >
          <span style={{ color: '#f85149' }}>
            −{negSum.toFixed(3)} drags
          </span>
          <span style={{ color: '#3fb950' }}>
            +{posSum.toFixed(3)} gains
          </span>
        </div>
      </button>
      {expanded ? (
        <table
          style={{
            width: '100%',
            marginTop: 4,
            borderCollapse: 'collapse',
            fontSize: 9,
            color: '#c9d1d9',
          }}
        >
          <tbody>
            {[...positives, ...negatives].map(([name, value]) => (
              <tr key={name}>
                <td style={{ padding: '1px 0', color: '#8b949e' }}>
                  <span
                    aria-hidden
                    style={{
                      display: 'inline-block',
                      width: 6,
                      height: 6,
                      borderRadius: 1,
                      marginRight: 5,
                      background: utilityColor(name, value < 0),
                      verticalAlign: 'middle',
                    }}
                  />
                  {name.replace(/_/g, ' ')}
                </td>
                <td
                  style={{
                    textAlign: 'right',
                    fontVariantNumeric: 'tabular-nums',
                    color: value >= 0 ? '#3fb950' : '#f85149',
                  }}
                >
                  {value >= 0 ? '+' : ''}
                  {value.toFixed(4)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </div>
  );
}

function prettyReason(raw: string): string {
  return raw
    .replace(/^hard_breach\((.+)\)$/, 'hard breach · $1')
    .replace(/^soft_breach\((.+)\)$/, 'soft breach · $1')
    .replace(/_/g, ' ');
}

const DISCIPLINE_COLOR: Record<string, string> = {
  low: '#ffd33d',
  medium: '#d29922',
  high: '#f85149',
  critical: '#ff3030',
};

function DisciplineRow({
  flag,
}: {
  flag: NonNullable<PodRuntimeState['disciplineFlag']>;
}) {
  const color = DISCIPLINE_COLOR[flag.severity] ?? '#8b949e';
  return (
    <div
      title={JSON.stringify(flag.detail, null, 2)}
      style={{
        marginTop: 4,
        padding: '6px 8px',
        background: `${color}22`,
        border: `1px solid ${color}`,
        borderRadius: 4,
        fontSize: 10,
        color: '#e6edf3',
        lineHeight: 1.35,
        cursor: 'help',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6 }}>
        <span style={{ fontWeight: 700, color }}>⚑ {flag.severity}</span>
        <span style={{ color: '#6e7681', fontSize: 9 }}>
          {relAge(Date.now() - new Date(flag.raisedAt).getTime())}
        </span>
      </div>
      <div style={{ color: '#c9d1d9' }}>{flag.code.replace(/_/g, ' ')}</div>
    </div>
  );
}

function CenterbookRow({
  ovr,
}: {
  ovr: NonNullable<PodRuntimeState['centerbook']>;
}) {
  const scaleColor = ovr.podScale < 0.9 ? '#f85149' : ovr.podScale > 1.05 ? '#3fb950' : '#e6edf3';
  return (
    <div
      style={{
        marginTop: 4,
        padding: '4px 8px',
        background: '#161b22',
        border: '1px solid #21262d',
        borderRadius: 4,
        fontSize: 10,
        color: '#c9d1d9',
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        alignItems: 'baseline',
      }}
      title={`gross before ${ovr.grossBefore?.toFixed(2) ?? '—'} → after ${ovr.grossAfter?.toFixed(2) ?? '—'}`}
    >
      <span style={{ color: '#8b949e' }}>Center book scale</span>
      <span style={{ color: scaleColor, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
        ×{ovr.podScale.toFixed(2)}
      </span>
    </div>
  );
}

function AllocationRow({
  alloc,
}: {
  alloc: NonNullable<PodRuntimeState['centerbookAllocation']>;
}) {
  // Center-book capital reallocation -- usually fires during daily
  // close to promote leaders and demote laggards.  Show the new
  // capital with a coloured delta chip so the move is unmistakable.
  const delta = alloc.deltaPct;
  const tone =
    delta == null
      ? '#8b949e'
      : delta > 0.02
        ? '#3fb950'
        : delta < -0.02
          ? '#f85149'
          : '#8b949e';
  const millions = alloc.capitalAfter / 1_000_000;
  return (
    <div
      title={
        [
          alloc.capitalBefore != null
            ? `before ₽${Math.round(alloc.capitalBefore).toLocaleString('en-US')}`
            : null,
          `after ₽${Math.round(alloc.capitalAfter).toLocaleString('en-US')}`,
          alloc.reason ? `reason: ${alloc.reason}` : null,
        ]
          .filter(Boolean)
          .join('\n')
      }
      style={{
        marginTop: 4,
        padding: '4px 8px',
        background: '#161b22',
        border: `1px solid ${tone}66`,
        borderRadius: 4,
        fontSize: 10,
        color: '#c9d1d9',
        display: 'grid',
        gridTemplateColumns: '1fr auto auto',
        gap: 6,
        alignItems: 'baseline',
      }}
    >
      <span style={{ color: '#8b949e' }}>Capital</span>
      <span
        style={{
          color: '#e6edf3',
          fontWeight: 700,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        ₽{millions.toFixed(2)}M
      </span>
      {delta != null ? (
        <span
          style={{
            color: tone,
            fontWeight: 700,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {delta >= 0 ? '+' : ''}
          {(delta * 100).toFixed(1)}%
        </span>
      ) : (
        <span style={{ color: '#6e7681' }}>new</span>
      )}
    </div>
  );
}

const POLICY_TONE: Record<string, { color: string; label: string; icon: string }> = {
  applied: { color: '#3fb950', label: 'applied', icon: '✓' },
  rolled_back: { color: '#a371f7', label: 'rolled back', icon: '↺' },
  shadow_recommended: { color: '#58a6ff', label: 'shadow', icon: '◐' },
  no_change: { color: '#8b949e', label: 'no change', icon: '·' },
};

function PolicyRow({ action }: { action: NonNullable<PodRuntimeState['policyAction']> }) {
  const tone = POLICY_TONE[action.kind] ?? POLICY_TONE.no_change!;
  const valueAfter =
    action.valueAfter == null
      ? null
      : typeof action.valueAfter === 'object'
        ? JSON.stringify(action.valueAfter)
        : String(action.valueAfter);
  const tip = [
    `rule: ${action.triggerRule}`,
    action.paramChanged ? `param: ${action.paramChanged}` : null,
    valueAfter ? `value: ${valueAfter}` : null,
    action.auditId != null ? `audit #${action.auditId}` : null,
  ]
    .filter(Boolean)
    .join('\n');
  return (
    <div
      title={tip}
      style={{
        marginTop: 4,
        padding: '4px 8px',
        background: '#161b22',
        border: `1px solid ${tone.color}66`,
        borderRadius: 4,
        fontSize: 10,
        color: '#c9d1d9',
        display: 'grid',
        gridTemplateColumns: 'auto 1fr auto',
        gap: 6,
        alignItems: 'baseline',
        cursor: action.auditId != null ? 'help' : 'default',
      }}
    >
      <span style={{ color: tone.color, fontWeight: 700 }} aria-hidden>
        {tone.icon}
      </span>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <span style={{ color: tone.color, fontSize: 9, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          policy · {tone.label}
        </span>
        <span style={{ color: '#e6edf3', fontWeight: 600 }}>
          {action.triggerRule.replace(/_/g, ' ')}
        </span>
        {action.paramChanged ? (
          <span style={{ color: '#8b949e', fontSize: 9 }}>
            {action.paramChanged}
            {valueAfter ? ` → ${valueAfter}` : ''}
          </span>
        ) : null}
      </div>
      <span style={{ color: '#6e7681', fontSize: 9 }}>
        {relAge(Date.now() - new Date(action.at).getTime())}
      </span>
    </div>
  );
}

/**
 * Best-effort parse of ``PodSummary.last_action`` (the REST snapshot's
 * latest PolicyUpdater action) into the same shape as the event-stream
 * reducer's ``policyAction``.  Falls back gracefully on unknown shapes.
 */
function parseLastActionFromBootstrap(
  raw: Record<string, unknown> | null,
): PodRuntimeState['policyAction'] {
  if (!raw) return null;
  const kindRaw =
    typeof raw.kind === 'string'
      ? raw.kind
      : typeof raw.action === 'string'
        ? raw.action
        : 'applied';
  const kind: PodPolicyAction['kind'] = (
    ['no_change', 'shadow_recommended', 'applied', 'rolled_back'] as const
  ).includes(kindRaw as PodPolicyAction['kind'])
    ? (kindRaw as PodPolicyAction['kind'])
    : 'applied';
  return {
    kind,
    triggerRule: typeof raw.trigger_rule === 'string' ? raw.trigger_rule : 'unknown',
    paramChanged: typeof raw.param_changed === 'string' ? raw.param_changed : null,
    valueAfter: raw.value_after,
    auditId: typeof raw.audit_id === 'number' ? raw.audit_id : null,
    at: typeof raw.at === 'string' ? raw.at : new Date().toISOString(),
  };
}

interface ModifierChip {
  key: string;
  value: string;
}

function collectModifiers(raw: Record<string, unknown> | null): ModifierChip[] {
  if (!raw) return [];
  const chips: ModifierChip[] = [];
  for (const [key, value] of Object.entries(raw)) {
    if (value == null || value === false || value === '') continue;
    if (typeof value === 'object') continue; // nested -- skip
    chips.push({
      key,
      value: value === true ? 'on' : String(value),
    });
    if (chips.length >= 6) break;
  }
  return chips;
}

function ModifiersChips({ modifiers }: { modifiers: ModifierChip[] }) {
  return (
    <div
      style={{
        marginTop: 4,
        display: 'flex',
        flexWrap: 'wrap',
        gap: 4,
      }}
    >
      {modifiers.map((m) => (
        <span
          key={m.key}
          title={`${m.key}: ${m.value}`}
          style={{
            fontSize: 9,
            padding: '2px 6px',
            borderRadius: 6,
            background: '#161b22',
            border: '1px solid #30363d',
            color: '#c9d1d9',
          }}
        >
          <span style={{ color: '#8b949e' }}>{m.key.replace(/_/g, ' ')}</span>
          {m.value === 'on' ? null : (
            <>
              <span style={{ color: '#6e7681' }}>: </span>
              <span style={{ fontWeight: 600 }}>{m.value}</span>
            </>
          )}
        </span>
      ))}
    </div>
  );
}
